import { ArrowRight, X, Check } from 'lucide-react';
import { Button, IconButton } from '@vnetwork/ui/primitives';
import { brand } from '@/config/brand';
import styles from './WafFreePromo.module.css';

/** Decorative WAAP artwork (light studio background — blends on the banner surface). */
const PROMO_IMAGE =
  'https://static.vncdn.vn/vnetwork.vn/pub/websites/uploads/1/83/huong-dan-kich-hoat-waf-free-thumb.png';


/** "Activate WAF Free in 3 steps" marketing guide — the auth-page banners open it. */
export const WAF_GUIDE_URL =
  'https://www.vnetwork.vn/en-US/news/huong-dan-kich-hoat-waf-free-3-buoc/';

/** Checklist of WAF Free benefits — each rendered as a pill with a brand check
 *  badge (per the team's campaign banner design). Shared with the auth-page brand
 *  panel so both surfaces list the same benefits. */
export const WAF_FREE_FEATURES = [
  'Block security vulnerability exploits',
  'Rate limiting',
  'Intuitive dashboard',
  'Block DDoS attacks',
  'Easy integration',
  'Faster content delivery',
] as const;

export interface WafPromoBannerProps {
  /** Fires on banner/CTA click (e.g. navigate to the WAAP onboarding launcher).
   *  Omit (or set hideCta) for a non-interactive, decorative banner. */
  onActivate?: () => void;
  /** When provided, renders the top-right dismiss button. */
  onDismiss?: () => void;
  /** Hide the CTA button and make the banner non-interactive — e.g. on the sign-up
   *  page, where "activate" has no meaning before the visitor has an account. */
  hideCta?: boolean;
  /** Activation is in-flight — spins the CTA and blocks re-clicks so a single tap
   *  provisions once (the banner triggers provisioning directly, not a page hop). */
  activating?: boolean;
  /** Drop the banner's own card chrome (border/shadow/radius/margin) and fill height,
   *  so it sits flush as one half of a split block. */
  flush?: boolean;
  /** CTA button label. Defaults to the in-app "Activate…" copy; auth pages pass a
   *  "Learn more" style label since the button opens the guide, not the activation. */
  ctaLabel?: string;
  /** Extra class on the outer container (page-specific spacing). */
  className?: string;
}

/**
 * Presentational WAAP promo banner — visuals only, no audience/dismiss gating.
 *
 * Reused by <WafFreePromo> (the in-app upsell, which adds eligibility + dismiss
 * gating) and the sign-up page's campaign panel. Container-responsive: it reflows
 * from a horizontal banner to a vertical card (artwork on top) once its OWN width
 * drops below the breakpoint — so it adapts inside a narrow column, not just on
 * small viewports (see WafFreePromo.module.css `@container`).
 */
export function WafPromoBanner({
  onActivate,
  onDismiss,
  hideCta,
  activating,
  flush,
  ctaLabel = 'Activate Web Application & API Protection',
  className,
}: WafPromoBannerProps) {
  const bannerClass = [styles.banner, flush ? styles.flush : '', className ?? '']
    .filter(Boolean)
    .join(' ');
  const interactive = !hideCta && !!onActivate && !activating;
  return (
    // Wrapper establishes the query container so the banner reflows by its OWN width
    // (a container query cannot restyle its own container element — the banner must
    // be a descendant of the container).
    //
    // data-theme="light" pins the whole promo to the light palette even on a dark
    // page: the brand artwork is a light studio render, so a themed (dark) surface
    // would show it as a bright patch. Re-declaring light --vn-* tokens on this
    // subtree keeps the banner a coherent light hero in both themes — no per-token
    // overrides, no hardcoded colors.
    <div className={styles.root} data-theme="light">
    {/* The whole banner is a click target for the CTA (pointer convenience); the inner
        Button keeps the keyboard/AT path. Dismiss (when present) stops propagation. */}
    <div
      className={bannerClass}
      role="region"
      aria-label="WAAP promotion"
      onClick={interactive ? onActivate : undefined}
      style={interactive ? undefined : { cursor: 'default' }}
    >
      {onDismiss ? (
        <IconButton
          className={styles.dismiss}
          variant="ghost"
          size="sm"
          aria-label="Dismiss WAAP promotion"
          onClick={(e) => {
            e.stopPropagation();
            onDismiss();
          }}
        >
          <X size={16} />
        </IconButton>
      ) : null}

      <div className={styles.textBlock}>
        <span className={styles.badge}>Security · WAF Free</span>

        <h2 className={styles.title}>Protect your website for free — today</h2>

        <p className={styles.subtitle}>
          Activate WAF Free — {brand.shortName}'s Web Application Firewall.
        </p>

        <ul className={styles.checkList}>
          {WAF_FREE_FEATURES.map((label) => (
            <li key={label} className={styles.checkItem}>
              <span className={styles.checkBadge} aria-hidden="true">
                <Check size={13} strokeWidth={3} />
              </span>
              {label}
            </li>
          ))}
        </ul>

        {!hideCta && (
          <div className={styles.ctaRow}>
            <span className={styles.ctaWrap}>
              <Button
                loading={activating}
                onClick={(e) => {
                  e.stopPropagation();
                  onActivate?.();
                }}
                // Hide the trailing arrow while the spinner shows (start slot), so the
                // CTA doesn't read as spinner + label + arrow all at once.
                endIcon={activating ? undefined : <ArrowRight size={15} />}
                className={styles.ctaBrandBtn}
              >
                {activating ? 'Activating…' : ctaLabel}
              </Button>
            </span>
          </div>
        )}
      </div>

      <div className={styles.illustration}>
        <img
          src={PROMO_IMAGE}
          alt=""
          loading="lazy"
          style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }}
        />
      </div>
    </div>
    </div>
  );
}

export default WafPromoBanner;
